//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Edit, TrashCan, Connect } from '@carbon/icons-react';
import SidePanel from './SidePanel';
import EditIntegrationPanel from './EditIntegrationPanel';
import SlackConnectionWizard from './SlackConnectionWizard';
import MCPTab from './MCPTab';
import CodingCLITab from './CodingCLITab';
import { notifyIngestionTriggered } from './ingestionTriggerBus';
import { useTranslation } from 'react-i18next';
import TenantDataService from '../../utils/TenantDataService';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useUserAuth } from '../../UserAuth';
import { WarningAlt, Copy } from '@carbon/icons-react';
import { Dialog, DialogContent, DialogClose } from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import AiDataService from '../../utils/AiDataService';
import { safeWarn } from '../../utils/securityUtils';
import mockData from './mockIn.json';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import type { StoredIntegration } from '../Sidebar/SidebarTypes';

// `StoredIntegration` is `IntegrationFormValues & {...}`, and
// `IntegrationFormValues` is a discriminated union of per-provider schemas —
// so most per-provider fields (apiEndpoint, model, apiKey, projectId)
// only exist on *some* branches. The list/table and
// `handleValidateModel` below read these generically across all providers,
// tolerating `undefined` for providers that structurally don't have them —
// that's legitimate optionality (e.g. an openai integration has no
// `apiEndpoint`), not a dead read of a field the redesign deleted. `type`/
// `mode` are legacy display-only fields still returned by the tenant API for
// the list table's "Credential Type"/"Integration Type" columns; they're not
// part of the redesigned form schema at all. Both are added back as an
// explicit, documented optional extension rather than narrowing every call
// site by provider, which is out of scope for this cleanup.
type IntegrationType = StoredIntegration & {
  type?: string;
  mode?: string;
  apiEndpoint?: string;
  model?: string;
  projectId?: string;
  apiKey?: string;
};

interface SlackConnection {
  connection_id: string;
  teamspace_id: string;
  name: string;
  webhook_url: string;
  status: string;
  has_signing_secret: boolean;
  has_bot_token: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

function Integration(): JSX.Element {
  const [isSidePanelVisible, setSidePanelVisible] = useState(false);
  const [isEditPanelVisible, setEditPanelVisible] = useState(false);
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationType | null>(null);
  const [editPanelMode, setEditPanelMode] = useState<'edit' | 'view'>('edit');
  const [integrations, setIntegrations] = useState<IntegrationType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { t } = useTranslation();
  const { teamspaceId } = useTeamspaceContext();
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<string | null>(
    null,
  );
  const { isAuthenticated, user } = useUserAuth();
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const [selectedIntegrationForMenu, setSelectedIntegrationForMenu] =
    useState<IntegrationType | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('integrations');
  const [isSlackWizardVisible, setSlackWizardVisible] = useState(false);
  const [slackConnections, setSlackConnections] = useState<SlackConnection[]>(
    [],
  );
  const [editSlackConnection, setEditSlackConnection] =
    useState<SlackConnection | null>(null);
  const [slackConnectionToDelete, setSlackConnectionToDelete] =
    useState<SlackConnection | null>(null);
  const [isDeleteSlackDialogOpen, setDeleteSlackDialogOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    success: boolean;
    title?: string;
    message: string;
  } | null>(null);

  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
  }, [user, isAuthenticated]);

  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
      ),
    [],
  );

  const aiDataService = useMemo(
    () =>
      new AiDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
      ),
    [],
  );

  const isNameUnique = (name: string, originalName?: string) => {
    return !integrations.some(
      (integration) =>
        integration.name.toLowerCase() === name.toLowerCase() &&
        (!originalName ||
          integration.name.toLowerCase() !== originalName.toLowerCase()),
    );
  };

  const fetchTeamspaceIntegrations = useCallback(async () => {
    try {
      const isDevelopment = process.env.NODE_ENV === 'development';
      if (!isDevelopment && !teamspaceId) {
        console.warn('Cannot fetch integrations: teamspaceId is missing');
        return;
      }

      const response = false
        ? mockData
        : await tenantDataService.getTeamspaceIntegrations(teamspaceId || '');

      const integrationsArray = response.integrations || [];

      const enabledMap: Record<string, boolean> = {};
      const transformedIntegrations: IntegrationType[] = integrationsArray.map(
        (item: any) => {
          let isenabled = item.isenabled || false;
          if (isenabled) {
            if (enabledMap[item.provider]) {
              isenabled = false;
            } else {
              enabledMap[item.provider] = true;
            }
          }
          // Spread the raw item and override only what this transform
          // computes (isenabled). Do NOT enumerate the rest of the fields
          // here — any field this list omits (e.g. Ollama's outputCeiling,
          // reasoning) is silently wiped on every enable/disable/delete
          // round-trip, since those actions spread this transformed object
          // back into the array they PUT to the server. `password`/
          // `confirmPassword`/`username` are excluded explicitly: they're
          // legacy credential fields not part of the current schema, and
          // toggle/delete resend this object to the server unvalidated —
          // unlike the edit-save path, which goes through the zod schema.
          const {
            password: _password,
            confirmPassword: _confirmPassword,
            username: _username,
            ...safeItem
          } = item;
          return {
            ...safeItem,
            isenabled,
          };
        },
      );

      setIntegrations(transformedIntegrations);
    } catch (error) {
      console.error('Error fetching teamspace integrations:', error);
    }
  }, [teamspaceId, tenantDataService]);

  const fetchSlackConnections = useCallback(async () => {
    try {
      if (!teamspaceId) return;
      const response = await aiDataService.getSlackConnections(teamspaceId);
      const connections = Array.isArray(response) ? response : [];
      setSlackConnections(connections);
    } catch (error) {
      console.error('Error fetching Slack connections:', error);
    }
  }, [teamspaceId, aiDataService]);

  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    if ((isAuthenticated && teamspaceId) || isDevelopment) {
      fetchTeamspaceIntegrations();
      fetchSlackConnections();
    }
  }, [
    isAuthenticated,
    teamspaceId,
    fetchTeamspaceIntegrations,
    fetchSlackConnections,
  ]);

  const closeSidePanel = () => {
    setSidePanelVisible(false);
    setSelectedIntegration(null);
    fetchTeamspaceIntegrations();
  };

  const deleteSlackConnection = async (connection: SlackConnection) => {
    try {
      await aiDataService.deleteSlackConnection(
        teamspaceId || '',
        connection.connection_id,
      );
      showSnackbar(
        `${t('integration')} ${connection.name} ${t('deleted-successfully')}`,
        'success',
      );
      fetchSlackConnections();
    } catch (error) {
      showSnackbar(t('error-deleting-integration'), 'error');
    }
  };

  const openSlackEdit = (connection: SlackConnection) => {
    setEditSlackConnection(connection);
    setSlackWizardVisible(true);
  };

  const handleSlackWizardClose = () => {
    setSlackWizardVisible(false);
    setEditSlackConnection(null);
    fetchSlackConnections();
  };

  const toggleIntegration = async (name: string) => {
    const selected = integrations.find(
      (integration) => integration.name === name,
    );
    if (!selected) return;

    const previousIntegrations = integrations;
    let updatedIntegrations: IntegrationType[];

    if (selected.isenabled) {
      updatedIntegrations = integrations.map((integration) =>
        integration.name === name
          ? { ...integration, isenabled: false }
          : integration,
      );
    } else {
      updatedIntegrations = integrations.map((integration) => ({
        ...integration,
        isenabled: integration.name === name,
      }));
    }

    // Optimistic update
    setIntegrations(updatedIntegrations);

    try {
      await tenantDataService.saveIntegrations(teamspaceId || '', {
        integrations: updatedIntegrations,
      });

      if (!selected.isenabled) {
        try {
          const ingestResponse = await aiDataService.triggerIngestion(
            teamspaceId || '',
            'True',
          );
          notifyIngestionTriggered(teamspaceId || '');
          if (ingestResponse?.success === false) {
            safeWarn('AI integration ingestion returned warning:', {
              integrationId: selected.id,
              integrationName: selected.name,
              success: ingestResponse?.success,
            });
          }
        } catch (ingestError) {
          safeWarn('AI integration ingestion failed after enable:', {
            integrationId: selected.id,
            integrationName: selected.name,
            error: String((ingestError as any)?.message || 'unknown'),
          });
        }
      }

      const statusText = !selected.isenabled
        ? t('activated')
        : t('deactivated');
      showSnackbar(`${t('integration')} ${name} ${statusText}`, 'success');
    } catch (error) {
      // Rollback on error
      setIntegrations(previousIntegrations);
      showSnackbar(t('error-updating-integration-status'), 'error');
    }
  };

  const openSidePanel = (integration?: IntegrationType) => {
    setSelectedIntegration(integration || null);
    setSidePanelVisible(true);
  };

  const openEditPanel = (
    integration: IntegrationType,
    mode: 'edit' | 'view',
  ) => {
    setSelectedIntegration(integration);
    setEditPanelMode(mode);
    setEditPanelVisible(true);
  };

  const closeEditPanel = () => {
    setEditPanelVisible(false);
    setSelectedIntegration(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const deleteIntegration = async (name: string) => {
    try {
      const updatedIntegrations = integrations.filter(
        (integration) => integration.name !== name,
      );

      const jsonData = {
        integrations: updatedIntegrations,
      };
      await tenantDataService.saveIntegrations(teamspaceId || '', jsonData);

      setIntegrations(updatedIntegrations);

      showSnackbar(
        `${t('integration')} ${name} ${t('deleted-successfully')}`,
        'success',
      );
    } catch (error) {
      showSnackbar(t('error-deleting-integration'), 'error');
    }
  };

  const handleMoreClick = (
    event: React.MouseEvent<HTMLElement>,
    integration: IntegrationType,
  ) => {
    setSelectedIntegrationForMenu(integration);
  };

  const handleMoreClose = () => {
    setSelectedIntegrationForMenu(null);
  };

  const handleValidateModel = async (integration: IntegrationType) => {
    setIsValidating(true);

    try {
      const payload = {
        id: integration.id,
        provider: integration.provider,
        name: integration.name,
        type: integration.type,
        isenabled: integration.isenabled,
        apiEndpoint: integration.apiEndpoint,
        mode: integration.mode,
        model: integration.model,
        projectId: integration.projectId,
        apiKey: integration.apiKey,
        teamspaceId: teamspaceId,
      };

      const response: any = await aiDataService.validateModel(payload);

      // For 200 response with ok: true
      if (response?.ok === true) {
        const providerLabel =
          integration.provider === 'openai'
            ? t('openAI')
            : integration.provider;
        showSnackbar(
          t('model-validated-successfully', { provider: providerLabel }),
          'success',
        );
      } else {
        // For ok: false, open modal with validation details
        setValidationResult({
          success: false,
          title:
            response?.error?.title || response?.title || t('validation-failed'),
          message:
            response?.error?.message ||
            response?.message ||
            t('unable-to-validate-model'),
        });
        setValidationDialogOpen(true);
      }
    } catch (error: any) {
      // Network errors or non-200 responses
      showSnackbar(t('unable-to-validate-model'), 'error');
    } finally {
      setIsValidating(false);
    }
  };

  const filteredIntegrations = integrations.filter(
    (integration) =>
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.provider.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredSlackConnections = slackConnections.filter(
    (conn) =>
      conn.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      'slack'.includes(searchQuery.toLowerCase()),
  );

  const hasAnyIntegration =
    integrations.length > 0 || slackConnections.length > 0;
  const hasSearchResults =
    filteredIntegrations.length > 0 || filteredSlackConnections.length > 0;

  const getSlackStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return t('active');
      case 'awaiting_credentials':
        return t('awaiting-credentials') || 'Awaiting Credentials';
      default:
        return t('not-connected') || 'Not Connected';
    }
  };

  const getSlackStatusClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[#029c54]/70 text-white';
      case 'awaiting_credentials':
        return 'bg-yellow-500/70 text-white';
      default:
        return 'bg-[#f52929]/70 text-white';
    }
  };

  return (
    <TooltipProvider>
      <div
        className="flex h-full flex-col bg-[#F4F4F4] dark:bg-[#161616]"
        id="id-integration-container"
        data-testid="integration-container"
      >
        <CustomSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          onClose={closeSnackbar}
        />

        {/* Content Area */}
        <div
          className="flex-1 overflow-auto px-4 pt-4"
          id="id-integration-content-area"
          data-testid="integration-content-area"
        >
          <div
            className="rounded-lg bg-white p-6 dark:bg-[#262626]"
            id="id-integration-card-wrapper"
            data-testid="integration-card-wrapper"
          >
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="integrations">
                  {t('integrations-tab') || 'Integrations'}
                </TabsTrigger>
                <TabsTrigger value="mcp">{t('mcp-tab') || 'MCP'}</TabsTrigger>
                <TabsTrigger value="coding-cli">
                  {t('coding-cli-tab') || 'Coding CLI'}
                </TabsTrigger>
              </TabsList>

              {/* Integrations Tab Content */}
              <TabsContent value="integrations">
                {/* Page Header with Button */}
                <div
                  className="mb-6 flex items-center justify-between gap-4"
                  id="id-integration-page-header"
                  data-testid="integration-page-header"
                >
                  <h2 className="text-base font-bold text-carbon-headingText dark:text-carbon-bodyTextDark">
                    {t('manage-integrations') || 'Manage Integrations'}
                  </h2>

                  <div
                    className="flex items-center gap-3"
                    id="id-integration-header-actions"
                    data-testid="integration-header-actions"
                  >
                    {/* Search Input - Inline when open, positioned left of icon */}
                    {isSearchOpen && (
                      <Input
                        type="text"
                        placeholder={t('search-integration')}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="max-w-xs border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-[#393939] dark:text-white"
                      />
                    )}

                    {/* Search Icon Button */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setIsSearchOpen(!isSearchOpen)}
                          className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                          aria-label={t('search-integration')}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="text-gray-500 dark:text-gray-400"
                          >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t('search-integration')}</TooltipContent>
                    </Tooltip>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button className="flex items-center gap-1 rounded bg-brand-blue text-white hover:bg-brand-nav dark:bg-brand-blue dark:hover:bg-brand-interactiveDark">
                          {t('add-integration') || 'Add Integration'}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem
                          onClick={() => setSlackWizardVisible(true)}
                        >
                          {t('slack') || 'Slack'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openSidePanel()}>
                          {t('ai-provider') || 'AI Provider'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Integrations Table */}
                {!hasAnyIntegration ? (
                  <div
                    className="flex flex-col items-center justify-center gap-3 py-16 text-center"
                    id="id-integration-empty-state"
                    data-testid="integration-empty-state"
                  >
                    <Connect
                      size={32}
                      className="text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                    <p className="text-sm font-medium text-carbon-headingText dark:text-white">
                      {t('no-integrations-yet') || 'No integrations added yet'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('no-integrations-hint') ||
                        'Click "Add Integration" above to connect a provider.'}
                    </p>
                  </div>
                ) : (
                  <div
                    className="table-container"
                    id="id-integration-table-container"
                    data-testid="integration-table-container"
                  >
                    <Table>
                      <TableHeader className="table-header">
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="font-bold"></TableHead>
                          <TableHead className="font-bold">
                            {t('provider')}
                          </TableHead>
                          <TableHead className="font-bold">
                            {t('name')}
                          </TableHead>
                          <TableHead className="font-bold">
                            {t('status')}
                          </TableHead>
                          <TableHead className="font-bold">
                            {t('actions')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredIntegrations.map((integration) => (
                          <TableRow
                            key={integration.id}
                            className="table-row last:border-0"
                          >
                            <TableCell className="table-cell">
                              <label className="relative inline-flex cursor-pointer items-center">
                                <input
                                  type="checkbox"
                                  className="peer sr-only"
                                  checked={integration.isenabled}
                                  onChange={() =>
                                    toggleIntegration(integration.name)
                                  }
                                  aria-label={
                                    integration.isenabled
                                      ? `${integration.provider} ${t('active')}`
                                      : `${integration.provider} ${t('inactive')}`
                                  }
                                />
                                <div
                                  id="id-integration-toggle-track"
                                  data-testid="integration-toggle-track"
                                  className={`peer h-4 w-8 rounded-full transition-colors ${integration.isenabled ? 'bg-[#0550dc]' : 'bg-gray-300 dark:bg-gray-600'}`}
                                >
                                  <div
                                    id="id-integration-toggle-thumb"
                                    data-testid="integration-toggle-thumb"
                                    className={`absolute left-0.5 top-0.5 size-3 rounded-full bg-white transition-transform ${integration.isenabled ? 'translate-x-4' : ''}`}
                                  ></div>
                                </div>
                              </label>
                            </TableCell>
                            <TableCell className="table-cell text-xs">
                              {integration.provider === 'openai'
                                ? t('openAI')
                                : integration.provider}
                            </TableCell>
                            <TableCell className="table-cell text-xs">
                              {integration.name}
                            </TableCell>
                            <TableCell className="table-cell">
                              <span
                                id="id-integration-status-badge"
                                data-testid="integration-status-badge"
                                className={`rounded-full px-3 py-1 text-xs font-normal ${
                                  integration.isenabled
                                    ? 'bg-[#029c54]/70 text-white'
                                    : 'bg-[#f52929]/70 text-white'
                                }`}
                              >
                                {integration.isenabled
                                  ? t('active')
                                  : t('inactive')}
                              </span>
                            </TableCell>
                            <TableCell className="table-cell">
                              <div
                                className="flex items-center gap-3"
                                id="id-integration-actions-cell"
                                data-testid="integration-actions-cell"
                              >
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() =>
                                        openEditPanel(integration, 'edit')
                                      }
                                      className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      aria-label={t('edit')}
                                    >
                                      <Edit
                                        size={18}
                                        className="text-gray-500 dark:text-gray-400"
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>{t('edit')}</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => {
                                        setIntegrationToDelete(
                                          integration.name,
                                        );
                                        setDeleteDialogOpen(true);
                                      }}
                                      className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      aria-label={t('delete')}
                                    >
                                      <TrashCan
                                        size={18}
                                        className="text-gray-500 dark:text-gray-400"
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>{t('delete')}</TooltipContent>
                                </Tooltip>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      aria-label={t('more')}
                                    >
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="text-gray-500 dark:text-gray-400"
                                      >
                                        <circle cx="12" cy="12" r="2" />
                                        <circle cx="19" cy="12" r="2" />
                                        <circle cx="5" cy="12" r="2" />
                                      </svg>
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleValidateModel(integration)
                                      }
                                      disabled={isValidating}
                                    >
                                      <Copy className="mr-2 size-4" />
                                      <span
                                        id="id-integration-validate-text"
                                        data-testid="integration-validate-text"
                                      >
                                        {isValidating
                                          ? t('validating')
                                          : t('validate')}
                                      </span>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredSlackConnections.map((conn) => (
                          <TableRow
                            key={`slack-${conn.connection_id}`}
                            className="table-row last:border-0"
                          >
                            <TableCell className="table-cell">
                              {/* Slack connections cannot be toggled; leave cell empty */}
                            </TableCell>
                            <TableCell className="table-cell text-xs">
                              {t('slack') || 'Slack'}
                            </TableCell>
                            <TableCell className="table-cell max-w-[200px] truncate text-xs">
                              {conn.name}
                            </TableCell>
                            <TableCell className="table-cell">
                              <span
                                className={`rounded-full px-3 py-1 text-xs font-normal ${getSlackStatusClass(conn.status)}`}
                              >
                                {getSlackStatusLabel(conn.status)}
                              </span>
                            </TableCell>
                            <TableCell className="table-cell">
                              <div className="flex items-center gap-3">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => openSlackEdit(conn)}
                                      className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      aria-label={t('edit')}
                                    >
                                      <Edit
                                        size={18}
                                        className="text-gray-500 dark:text-gray-400"
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>{t('edit')}</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => {
                                        setSlackConnectionToDelete(conn);
                                        setDeleteSlackDialogOpen(true);
                                      }}
                                      className="rounded p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      aria-label={t('delete')}
                                    >
                                      <TrashCan
                                        size={18}
                                        className="text-gray-500 dark:text-gray-400"
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>{t('delete')}</TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!hasSearchResults && (
                          <TableRow className="hover:bg-transparent">
                            <TableCell
                              colSpan={5}
                              className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                              role="status"
                              aria-live="polite"
                            >
                              {t('no-integrations-found', {
                                query: searchQuery,
                              }) ||
                                `No integrations found for "${searchQuery}"`}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* MCP Tab Content */}
              <TabsContent value="mcp">
                <MCPTab teamspaceId={teamspaceId} />
              </TabsContent>

              {/* Coding CLI Tab Content */}
              <TabsContent value="coding-cli">
                <CodingCLITab />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <Dialog
          open={validationDialogOpen}
          onOpenChange={setValidationDialogOpen}
        >
          <DialogContent>
            <div
              className="mb-4 flex items-center justify-between"
              id="id-integration-validation-dialog-header"
              data-testid="integration-validation-dialog-header"
            >
              <h2 className="text-lg font-semibold">
                {validationResult?.title}
              </h2>
              <DialogClose
                className="text-gray-500 hover:text-gray-700"
                aria-label={t('close')}
              >
                ✕
              </DialogClose>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {validationResult?.message}
            </p>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="pt-6">
            <div
              className="flex items-start gap-4"
              id="id-integration-delete-dialog-content"
              data-testid="integration-delete-dialog-content"
            >
              <WarningAlt size={20} className="mt-1 text-[#f52929]" />
              <div
                id="id-integration-delete-dialog-text"
                data-testid="integration-delete-dialog-text"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t('confirm-deletion') || 'Confirm Deletion'}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t('are-you-sure-you-want-to-delete-this-integration') ||
                    'Are you sure you want to delete this integration?'}
                </p>
              </div>
            </div>
            <div
              className="mt-6 flex justify-end gap-2"
              id="id-integration-delete-dialog-actions"
              data-testid="integration-delete-dialog-actions"
            >
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                variant="outline"
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={() => {
                  if (integrationToDelete) {
                    deleteIntegration(integrationToDelete);
                  }
                  setDeleteDialogOpen(false);
                  setIntegrationToDelete(null);
                }}
                className="bg-[#f52929] text-white hover:bg-[#da1e28]"
              >
                {t('yes-delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <SidePanel
          isVisible={isSidePanelVisible}
          onClose={closeSidePanel}
          isNameUnique={isNameUnique}
        />

        <EditIntegrationPanel
          isVisible={isEditPanelVisible}
          onClose={closeEditPanel}
          integrationData={selectedIntegration}
          mode={editPanelMode}
          fetchIntegrations={fetchTeamspaceIntegrations}
          integrations={integrations}
        />

        <Dialog
          open={isDeleteSlackDialogOpen}
          onOpenChange={setDeleteSlackDialogOpen}
        >
          <DialogContent className="pt-6">
            <div className="flex items-start gap-4">
              <WarningAlt size={20} className="mt-1 text-[#f52929]" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t('confirm-deletion') || 'Confirm Deletion'}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t('are-you-sure-you-want-to-delete-this-integration') ||
                    'Are you sure you want to delete this integration?'}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                onClick={() => setDeleteSlackDialogOpen(false)}
                variant="outline"
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={() => {
                  if (slackConnectionToDelete) {
                    deleteSlackConnection(slackConnectionToDelete);
                  }
                  setDeleteSlackDialogOpen(false);
                  setSlackConnectionToDelete(null);
                }}
                className="bg-[#f52929] text-white hover:bg-[#da1e28]"
              >
                {t('yes-delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <SlackConnectionWizard
          isVisible={isSlackWizardVisible}
          onClose={handleSlackWizardClose}
          editConnection={editSlackConnection}
        />
      </div>
    </TooltipProvider>
  );
}

export default Integration;
